import smartReplyService from '@/modules/bizai/services/smartReplyService';
import poll from '@/utils/poll';

const state = {
  isLoading: false,
  currentMatterUid: null, // Track which matter we're loading for
  currentRequestId: null, // Track which request we're processing to prevent stale data
  replyContent: {
    email_message: '',
    FB_message: '',
    other_message: '',
    sms_message: '',
  },
};

const getters = {
  isLoading: (state) => state.isLoading,
  replyContent: (state) => state.replyContent,
  currentRequestId: (state) => state.currentRequestId,
};

const mutations = {
  setLoading(state, isLoading) {
    state.isLoading = Boolean(isLoading);
  },
  setCurrentMatterUid(state, matterUid) {
    state.currentMatterUid = matterUid;
  },
  setCurrentRequestId(state, requestId) {
    state.currentRequestId = requestId;
  },
  setReplyContent(state, content) {
    state.replyContent = {
      email_message: content.email_message || '',
      FB_message: content.FB_message || '',
      other_message: content.other_message || '',
      sms_message: content.sms_message || '',
    };
  },
  clearReplyContent(state) {
    state.currentMatterUid = null;
    state.currentRequestId = null;
    state.replyContent = {
      email_message: '',
      FB_message: '',
      other_message: '',
      sms_message: '',
    };
  },
};

const actions = {
  async loadSmartReply({ commit, state }, payload) {
    const matterUid = typeof payload === 'string' ? payload : payload.matterUid;
    const requestId = typeof payload === 'object' ? payload.requestId : null;

    commit('setCurrentMatterUid', matterUid);
    if (requestId) {
      commit('setCurrentRequestId', requestId);
    }
    commit('setLoading', true);
    
    const smartReplies = await poll(
      async () => {
        if (state.currentMatterUid !== matterUid || (requestId && state.currentRequestId !== requestId)) {
          return { cancelled: true };
        }
        
        try {
          const replies = await smartReplyService.listSmartReplies(matterUid, 1, 1);
          return (replies && replies.length > 0) ? replies : null;
        } catch (error) {
          console.error('[POV SmartReplyStore] Error fetching smart replies:', error);
          return null;
        }
      },
      (result) => {
        if (result?.cancelled) return true;
        return !!(result && result.length > 0);
      },
      3000,
      15000, // 15s timeout - checking if replies exist
    );
    
    if (smartReplies?.cancelled) {
      commit('setLoading', false);
      return null;
    }
    
    const isSmartReplyAvailable = smartReplies && smartReplies.length > 0;
    if (isSmartReplyAvailable) {
      const latestReply = smartReplies[0];
      const replyContent = latestReply?.payload || latestReply;
      commit('setReplyContent', replyContent);
      commit('setLoading', false);
      return latestReply;
    }

    commit('setReplyContent', {
      email_message: '',
      FB_message: '',
      other_message: '',
      sms_message: '',
    });
    commit('setLoading', false);
    return null;
  },
  async generateSmartReply({ commit, state }, { matterUid, clientUid, userInputReferenceId = 'prmpt-smart-reply-00001', populatedText, customInstructions, requestId }) {
    commit('setCurrentMatterUid', matterUid);
    if (requestId) {
      commit('setCurrentRequestId', requestId);
    }
    commit('setLoading', true);
    try {
      const smartReply = await smartReplyService.createSmartReply(
        matterUid,
        clientUid,
        userInputReferenceId,
        populatedText,
        customInstructions,
      );

      if (state.currentMatterUid !== matterUid || (requestId && state.currentRequestId !== requestId)) {
        return null;
      }

      const replyContent = smartReply?.payload || smartReply;
      commit('setReplyContent', replyContent);
      return smartReply;
    } catch (error) {
      console.error('[POV SmartReplyStore] Error in generateSmartReply:', error);
      return error;
    } finally {
      commit('setLoading', false);
    }
  },

  clearSmartReply({ commit }) {
    commit('clearReplyContent');
  },

  setReplyContent({ commit }, replyContent) {
    if (replyContent && typeof replyContent === 'object') {
      commit('setReplyContent', replyContent);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
